import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

const Header = ({ sections }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  // Handle scroll detection for header background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Set up intersection observer for section detection
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-50% 0px',  // Trigger when section is in the middle of viewport
      threshold: 0
    };

    const handleIntersect = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    // Observe all sections
    sections.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [sections]);

  // Handle navigation click
  const handleNavClick = (ref) => {
    setIsMobileMenuOpen(false); // Close mobile menu
    ref.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen
          ? 'bg-slate-900/80 backdrop-blur-sm shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-6 py-6 md:py-4">
        {/* Logo */}
        <div className="flex items-center gap-4">
          <button 
            onClick={() => handleNavClick(sections[0].ref)}
            className="flex items-center gap-2"
          >
            <img 
              src="/images/logo.png" 
              alt="Company Logo" 
              className="w-auto h-12 md:h-16 object-contain invert brightness-0 filter" 
            />
          </button>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex gap-8 text-lg font-semibold">
          {sections.map(({ id, ref, label }) => (
            <button 
              key={id}
              onClick={() => handleNavClick(ref)}
              className={`transition-colors duration-300 relative group ${
                activeSection === id
                  ? 'text-blue-400'
                  : 'text-white hover:text-blue-300'
              }`}
            >
              {label}
              <span 
                className={`absolute bottom-0 left-0 w-full h-0.5 transform origin-left transition-transform duration-300 ${
                  activeSection === id
                    ? 'bg-blue-400 scale-x-100'
                    : 'bg-blue-300 scale-x-0 group-hover:scale-x-100'
                }`}
              />
            </button>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden text-white p-2 -mr-2"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-slate-900/95 backdrop-blur-sm md:hidden shadow-lg">
            <div className="flex flex-col items-center py-8">
              {sections.map(({ id, ref, label }, index) => (
                <div key={id} className="w-full">
                  <button 
                    onClick={() => handleNavClick(ref)}
                    className={`text-xl font-semibold transition-colors duration-300 px-8 py-4 w-full block text-center ${
                      activeSection === id
                        ? 'text-blue-400'
                        : 'text-white hover:text-blue-300'
                    }`}
                  >
                    {label}
                  </button>
                  {/* Add divider except for last item */}
                  {index !== sections.length - 1 && (
                    <div className="my-4 w-16 mx-auto border-t border-slate-700" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;